/**
 * Magma Pawn Component
 *
 * SVG character pawn used by the magma skin. Depicts a chameleon/frog creature
 * with a silver/grey head and foliage accents, and a colored body sphere.
 *
 * Based on the Figma design (green_pawn.svg). Color-specific gradients (body sphere,
 * neck section, foliage accents) are parameterized via MagmaPawnColorScheme.
 * Neutral elements (silver head gradient, white spot highlights, dark shadow areas)
 * are shared across all color variants.
 *
 * ViewBox: 0 0 323 473 (matches crypt pawn for consistent road section scaling)
 */

import React from 'react';
import type { PawnProps } from '../../constants/types';
import type { MagmaPawnColorScheme } from './constants/pawnColorSchemes';

interface MagmaPawnInternalProps extends PawnProps {
  colorScheme: MagmaPawnColorScheme;
}

const MagmaPawnComponent = React.memo(function MagmaPawnComponent({
  viewBox = '0 0 323 473',
  className = '',
  colorScheme
}: MagmaPawnInternalProps) {
  const prefix = `${colorScheme.name}MagmaPawn`;

  return (
    <svg
      viewBox={viewBox}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${colorScheme.name} magma pawn`}
    >
      {/* Outer shell shadow (neutral: transparent to black) */}
      <path
        d="M158.81 446.66C95.25 446.66 43.55 398.68 43.55 339.71C43.55 325.12 46.6601 310.98 52.7901 297.67C55.2301 276.72 63.61 257.25 77.09 241.18C75.99 239.97 74.9201 238.73 73.8301 237.41C62.9601 224.2 55.41 208.41 51.98 191.74C50.29 183.65 49.58 175.27 49.85 166.83C50.39 150.26 54.66 134.5 62.53 120.01C70.12 105.99 81.01 93.67 94.02 84.36C112.02 71.46 133.25 64.64 155.41 64.64C156.59 64.64 157.77 64.66 158.95 64.7C165.99 64.94 172.88 65.84 179.43 67.39C185.63 68.83 191.72 70.84 197.53 73.36C203.14 75.79 208.69 78.81 214 82.33C223.97 88.95 232.68 97.17 239.89 106.75C247.17 116.41 252.69 127.14 256.31 138.65L256.45 139.09V139.11C256.95 140.7 257.42 142.36 257.88 144.16C260.05 152.67 261.15 161.45 261.15 170.27C261.15 171.44 261.13 172.62 261.09 173.81C260.56 189.97 256.49 205.37 248.99 219.58C247.12 223.13 245.01 226.64 242.7 229.99C240.61 233.04 238.36 235.97 236 238.74C249.11 253.46 257.86 271.41 261.38 290.85C269.69 305.87 274.08 322.75 274.08 339.72C274.08 398.69 222.37 446.67 158.82 446.67L158.81 446.66Z"
        fill={`url(#${prefix}_outerShadow)`}
      />

      {/* Body outer sphere (color-specific) */}
      <path
        d="M158.81 443.67C220.815 443.67 271.08 397.121 271.08 339.7C271.08 282.279 220.815 235.73 158.81 235.73C96.805 235.73 46.54 282.279 46.54 339.7C46.54 397.121 96.805 443.67 158.81 443.67Z"
        fill={`url(#${prefix}_bodyOuter)`}
      />

      {/* Body dark shadow ring (player-specific dark tone) */}
      <path
        d="M157.59 424.2C214.216 424.2 260.12 377.651 260.12 320.23C260.12 262.809 214.216 216.26 157.59 216.26C100.964 216.26 55.0601 262.809 55.0601 320.23C55.0601 377.651 100.964 424.2 157.59 424.2Z"
        fill={colorScheme.bodyDarkShadow}
      />

      {/* Body dark ring shadow fade (neutral: transparent to black) */}
      <path
        d="M157.59 414.32C214.216 414.32 260.12 367.771 260.12 310.35C260.12 252.929 214.216 206.38 157.59 206.38C100.964 206.38 55.0601 252.929 55.0601 310.35C55.0601 367.771 100.964 414.32 157.59 414.32Z"
        fill={`url(#${prefix}_bodyDarkFade)`}
      />

      {/* Body inner bright sphere (color-specific) */}
      <path
        d="M159.17 370.59C120.52 370.59 89.0801 338.51 89.0801 299.08C89.0801 259.65 120.52 227.57 159.17 227.57C197.82 227.57 229.26 259.65 229.26 299.08C229.26 338.51 197.82 370.59 159.17 370.59Z"
        fill={`url(#${prefix}_bodyInner)`}
      />

      {/* Body center dark ring (neutral: black) */}
      <path
        d="M158.81 368.59C199.033 368.59 231.64 335.526 231.64 294.74C231.64 253.954 199.033 220.89 158.81 220.89C118.587 220.89 85.98 253.954 85.98 294.74C85.98 335.526 118.587 368.59 158.81 368.59Z"
        fill="black"
      />

      {/* Body front sphere (color-specific) */}
      <path
        d="M158.81 364.54C199.033 364.54 231.64 331.476 231.64 290.69C231.64 249.904 199.033 216.84 158.81 216.84C118.587 216.84 85.98 249.904 85.98 290.69C85.98 331.476 118.587 364.54 158.81 364.54Z"
        fill={`url(#${prefix}_bodyFront)`}
      />

      {/* Foliage/leaf accent band (bright, player-specific) */}
      <path
        d="M85.86 225.69L115.48 316.36C121.45 334.65 136.95 347.02 154.62 347.6C174.5 348.25 192.3 333.89 197.77 312.78L221 223.12C228.78 193.08 208.75 163.22 180.44 162.68L127.59 161.67C97.22 161.09 75.59 194.27 85.85 225.69H85.86Z"
        fill={colorScheme.detailLight}
      />

      {/* Neck/torso section (color-specific gradient) */}
      <path
        d="M88.6601 220.34L117.06 310.97C122.79 329.25 137.69 341.6 154.69 342.16C173.82 342.79 190.98 328.4 196.27 307.29L218.74 217.61C226.27 187.56 207.02 157.73 179.78 157.22L128.91 156.27C99.6801 155.72 78.8201 188.93 88.6601 220.34Z"
        fill={`url(#${prefix}_neck)`}
      />

      {/* Head underside dark area (neutral) */}
      <path
        d="M95.24 137.57L120.76 141.45L192.15 140.69L201.29 142.45L207.88 166.66L227.29 195.63L241.89 228.77L187.87 235.4L129.44 201.52L99.43 171.34L95.24 137.57Z"
        fill="#232323"
      />

      {/* Right arm/fin (neutral: silver gradient) */}
      <path
        d="M208.69 153.68L213.67 168.57L218.8 169.72L221.73 188.12L231.62 189.26L233.64 203.52L241.64 207.42L238.59 221.73L232.34 213.97L220.64 196.33L210.71 177.74L198.44 155.89L194.83 143.83L207.28 138.85L208.69 153.68Z"
        fill={`url(#${prefix}_finRight)`}
      />

      {/* Foliage accent band - bright primary (player-specific) */}
      <path
        d="M105.36 186.22C101.53 184.46 95.6101 181.76 92.6901 177.42C91.5001 175.66 91.3101 174.37 91.3101 172.82L97.9601 149.87C103.4 149.3 109.89 148.37 116.18 146.23C124.22 143.49 124.11 141.42 130.6 141.08C137.38 140.72 146.09 142.52 153.09 146.19C160.7 150.18 159.36 152.75 170.21 159.31C173.47 161.28 176.67 163.21 181.4 165.35C185.76 167.32 189.43 168.57 194.05 171.58C195.87 172.76 199.06 174.84 198.56 175.68C197.9 176.8 191.13 175.1 189.34 174.65C189.76 175.79 190.54 177.42 192.21 179.21C194.64 181.81 197.24 182.93 200.75 185.18C206.61 188.92 205.42 189.64 210.33 192.85C212.83 194.49 221.6 200.22 227.92 199.12C231.19 198.54 233.25 196.22 232.89 193.79C232.37 190.41 227.23 187.65 222.8 185.27C218.06 182.73 213.63 181.36 210.83 180.64C212.95 180.33 216.24 180.02 220.41 180.39C236.02 181.75 247.81 190.99 250.88 193.41C251.75 194.09 263.8 203.65 260.72 206.75C259.39 208.09 255.56 207.9 252.06 207.74C246.64 207.48 245.72 206.31 241.09 206.19C235.6 206.05 235.18 207.66 230.91 207.21C227.25 206.82 225.83 205.46 219.99 202.88C215.86 201.06 208.03 197.62 200.39 196.97C194.46 196.46 195.27 198.21 187.38 198.57C182.89 198.77 176.56 197.68 163.91 195.5C155.77 194.09 152.19 193.27 150.63 191.42C149.35 189.89 150.77 189.24 149.93 187.26C148.24 183.25 139.81 179.63 133.09 178.42C128.4 177.58 121.04 177.29 120.2 179.58C119.56 181.33 122.91 184.05 125.22 185.93C129.35 189.3 132.39 190.03 132.18 190.75C131.62 192.73 117.21 191.66 105.34 186.24L105.36 186.22Z"
        fill={colorScheme.accentBright}
      />

      {/* Foliage detail band - dark mid (player-specific) */}
      <path
        d="M95.07 165.28C94.62 164.97 94.19 164.65 93.79 164.3L96.95 153.38C97.82 153.42 98.84 153.45 100.02 153.43C105.93 153.38 110.09 152.49 112.62 151.95C118.95 150.61 119.19 149.42 125.44 148.44C128.52 147.96 130.82 147.59 133.62 147.78C139.18 148.15 141.86 150.36 154.93 156.98C163.16 161.15 170.54 166.42 179.37 169.78C180.22 170.1 182.99 171.12 183.54 172.49C184 173.65 182.44 173.94 182.79 175.07C183.2 176.41 185.74 177.04 189.71 179.4C191.97 180.75 195.42 182.8 195.1 183.76C194.81 184.67 191.28 184.33 183.06 183.3C169.91 181.66 163.28 180.8 160.19 179.48C153.92 176.78 155.08 175.08 147.27 171.54C144.03 170.08 137.68 167.26 130.94 167.51C127.72 167.63 122.61 168.52 122.37 170.86C122.21 172.3 124.01 173.43 123.38 173.94C122.41 174.73 117.68 172.6 111.1 170.38C100.06 166.66 98.52 167.62 95.08 165.27L95.07 165.28Z"
        fill={colorScheme.detailMid}
      />

      {/* Foliage detail band - light (player-specific) */}
      <path
        d="M95.02 160.04L95.79 157.4C96.56 157.17 97.66 156.98 99.06 156.75C107.6 155.36 114.81 154.33 115.38 154.26C126.04 152.74 124.4 150.44 130.48 150.7C138.42 151.05 145.77 155.13 150.18 157.58C157.29 161.52 155.77 162.48 165 168.17C171.95 172.45 177.55 174.82 177.09 176.33C176.84 177.15 174.98 177.27 173.42 177.37C170.78 177.54 167.31 177.11 161.88 174.96C156.95 173.01 156.08 171.88 151.62 169.66C147.92 167.82 139.16 163.46 131.27 163.76C126.88 163.93 127.65 165.35 121 166.38C117.19 166.98 112.57 167.69 106.96 166.28C101.92 165.01 96.81 162.21 95.02 160.05V160.04Z"
        fill={colorScheme.detailBright}
      />

      {/* White spot highlight - left inner (neutral) */}
      <path
        d="M134.19 192.71C133.88 189.98 135.11 187.36 136.11 185.24C137.4 182.51 139.64 177.77 143 177.31C148.04 176.63 153.1 185.87 155.04 191.91C156.19 195.48 158.19 201.7 155.51 204.08C151.19 207.93 135.18 201.3 134.19 192.7V192.71Z"
        fill="#FFFFFA"
      />

      {/* White spot highlight - center (neutral) */}
      <path
        d="M158.34 205.78C156.22 201.59 158.41 196.71 158.8 195.86C159.31 194.74 161.7 189.43 166.35 188.87C172.01 188.18 176.05 195.11 176.66 196.17C176.9 196.59 181.41 204.52 178.02 208.92C174.18 213.89 161.71 212.44 158.34 205.78Z"
        fill="#FFFFFA"
      />

      {/* White spot highlight - right outer (neutral) */}
      <path
        d="M185.17 215.05C184.58 211.48 187.64 207.61 189.39 205.4C191.56 202.66 192.91 200.94 194.97 200.67C201.4 199.82 206.81 214.12 208.37 220.19C208.66 221.31 208.83 222.21 208.35 222.96C205.76 226.99 186.5 223.06 185.17 215.05Z"
        fill="#FFFFFA"
      />

      {/* White spot - top right (neutral) */}
      <path
        d="M230.64 143.14C229.5 143.87 224.68 147.21 224.37 153.23C224.18 156.83 225.58 161.93 228.81 163.17C235.77 165.85 250.74 150.46 248.1 144.4C246.41 140.53 236.96 139.09 230.64 143.14Z"
        fill="#FFFFFA"
      />

      {/* White spot - upper right (neutral) */}
      <path
        d="M198.91 149.01C198 153.1 201.16 157.35 201.81 158.24C202.82 159.6 205.5 163.21 208.56 163.22C215.2 163.24 222.87 146.3 218.52 141.76C214.76 137.83 200.54 141.63 198.9 149.01H198.91Z"
        fill="#FFFFFA"
      />

      {/* White spot - upper left (neutral) */}
      <path
        d="M141.82 145.14C140.64 147.93 144.26 152.16 146.98 154.12C147.68 154.63 154.05 159.09 159.24 155.72C162.61 153.54 164.45 148.73 163.02 145.7C160.15 139.59 143.86 140.35 141.83 145.14H141.82Z"
        fill="#FFFFFA"
      />

      {/* White spot - top center (neutral) */}
      <path
        d="M170.83 141.16C166.82 144.74 168.17 151.82 168.28 152.35C169.54 158.51 174.5 161.45 175.28 161.89C176.57 161.73 190.16 159.85 192.75 151.79C193.25 150.24 193.11 149.18 193.06 148.86C191.95 141.95 177 135.66 170.83 141.16Z"
        fill="#FFFFFA"
      />

      {/* White spot - right cheek (neutral) */}
      <path
        d="M199.32 169.64C200.14 165.85 203.02 163.75 204.46 162.7C206.97 160.87 209.48 160.24 210.91 159.99C211.59 161.76 212.64 165.2 212.13 169.5C211.65 173.51 210.01 176.6 208.55 178.65C207.24 180.49 204.65 181 202.82 179.7C202.81 179.7 202.79 179.68 202.78 179.67C199.62 177.4 198.59 173.08 199.33 169.64H199.32Z"
        fill="#FFFFFA"
      />

      {/* White spot - small right side (neutral) */}
      <path
        d="M216.35 222.07C215.99 221.71 210.78 216.31 211.37 210.14C211.49 208.87 211.78 208.39 212.16 208.18C213.64 207.37 216.9 210.41 218.57 212.38C222.84 217.42 224.77 224.98 222.95 226.12C221.35 227.12 217.2 222.93 216.35 222.07Z"
        fill="#FFFFFA"
      />

      {/* Neck underside shadow overlay (neutral) */}
      <path
        opacity="0.5"
        d="M92.1 161.66C92.1 161.66 109.22 180.76 131.21 193.62C131.21 193.62 141.38 203.81 149.42 201.27C157.47 198.72 152.98 205.41 152.98 205.41L159.8 208.66C159.8 208.66 168.43 219.29 181.16 215.73L185.08 214.93L194.47 224.33C194.47 224.33 206.18 231.22 216.49 224.92C216.49 224.92 217.62 238.85 214.85 248.02C212.13 257.02 211.01 261.68 211.01 261.68L171.5 242.16L83.3 211.23C83.3 211.23 82.9599 208.6 83.8399 201.92C85.3399 190.55 89.57 184.42 89.57 184.42"
        fill="#232323"
      />

      {/* Left leaf decoration stem (neutral: dark, solid) */}
      <path d="M196.82 96.41L247.46 103.53L237.21 111.06L210.44 104.53" fill="#232323" />

      {/* Head/body main silhouette (neutral: silver gradient) */}
      <path
        d="M96.82 79.52L105.82 77.59L131.01 61.54L196.82 87.91L247.45 95.03L237.2 102.55L210.43 96.03L187.92 113.14C188.16 112.51 194.22 118.42 188 151.59L190.35 156.81L168.91 144.64L163.08 153.67L146.31 144.18L138.94 155.58C138.94 155.58 114.55 156.32 98.62 145.01C98.62 145.01 103.25 172.25 125.24 185.12C125.24 185.12 135.41 195.31 143.45 192.77C151.5 190.22 150.5 191.29 150.5 191.29L153.84 200.15C153.84 200.15 162.47 210.78 175.2 207.22L179.12 206.42L188.51 215.82C188.51 215.82 200.22 222.71 210.53 216.41C210.53 216.41 211.03 227.69 211.66 238.58C212.27 249.06 212.01 256.6 212.01 256.6L165.55 233.65L82.77 203.56L60.49 151.27L74.73 131.84L96.84 79.52H96.82Z"
        fill={`url(#${prefix}_head)`}
      />

      {/* Left neck side highlight (neutral: silver gradient) */}
      <path
        d="M190.35 156.81C190.35 156.81 209.66 145.29 234.28 145.58L237.21 102.55L210.44 96.03L187.93 113.14L186.58 132L186.79 141.84C186.85 144.89 187.18 147.93 187.77 150.92L190.36 156.81H190.35Z"
        fill={`url(#${prefix}_neckLeft)`}
      />

      {/* Right ear/spine fin (neutral: silver gradient) */}
      <path
        d="M251.95 109.99C252.24 111.59 252.54 113.36 252.85 115.32C253.15 117.22 253.45 119.28 253.77 121.54C257.33 147.17 247.94 156.75 247.94 156.75C247.94 156.75 247.69 155.58 248.42 149.49C249.15 143.39 234.28 145.57 234.28 145.57L237.2 102.55L247.46 95.03C247.46 95.03 249.31 95.63 251.94 109.99H251.95Z"
        fill={`url(#${prefix}_earFin)`}
      />

      {/* Tail/lower fin (neutral: silver gradient) */}
      <path
        d="M210.53 216.4C210.53 216.4 220.75 225.4 230.68 220.2C240.61 215 241.64 207.41 241.64 207.41L249.75 245.15C249.75 245.15 238.75 241.58 229.33 246.27C219.9 250.96 212.01 256.59 212.01 256.59C212.01 256.59 208.29 227.37 210.53 216.4Z"
        fill={`url(#${prefix}_tailFin)`}
      />

      {/* Left leaf/wing decoration - bright (player-specific) */}
      <path
        d="M59.61 36.3199L65.98 65.9499C65.98 65.9499 66.79 78.2199 94.69 84.5699L84.82 107.94L46.79 88.2499C46.79 88.2499 39.72 80.8099 59.62 36.3299L59.61 36.3199Z"
        fill={colorScheme.bodyInnerMid}
      />

      {/* Left leaf/wing decoration - dark (player-specific) */}
      <path
        d="M77.81 124.52L44.25 97.6199C44.25 97.6199 29.22 87.6499 59.62 36.3199L51.4 56.9999C51.4 56.9999 42.74 85.0999 48.36 88.1499C53.98 91.1999 84.83 107.93 84.83 107.93L77.82 124.53L77.81 124.52Z"
        fill={colorScheme.accentDark}
      />

      {/* Eye socket white (neutral) */}
      <path
        d="M130.33 101.47C125.51 102.54 122.44 105.94 121.41 107.07C120.67 107.88 114.28 114.94 116.49 121.32C118.81 128.01 129.19 129.95 136.19 128.75C143.19 127.55 152.13 122.32 152.31 115.47C152.53 107.19 139.89 99.34 130.33 101.47Z"
        fill="#FFFFFA"
      />

      {/* Right leaf/wing decoration - bright (player-specific) */}
      <path
        d="M187.98 84.36L141.78 35.74L162.13 69.02L165.89 75.51L187.98 84.36Z"
        fill={colorScheme.bodyInnerMid}
      />

      {/* Right leaf/wing decoration - dark (player-specific) */}
      <path
        d="M154.3 70.86C154.3 70.86 151.16 49.38 141.79 35.74C141.79 35.74 162.27 60.47 165.9 75.51L154.31 70.87L154.3 70.86Z"
        fill={colorScheme.accentDark}
      />

      {/* Eye pupil outline (neutral: black) */}
      <path
        d="M140.516 124.076C145.052 123.101 148.033 119.072 147.174 115.076C146.315 111.081 141.942 108.632 137.406 109.607C132.871 110.581 129.89 114.611 130.749 118.606C131.608 122.602 135.981 125.051 140.516 124.076Z"
        fill="black"
      />

      {/* Eye pupil fill (player-specific) */}
      <path
        d="M142.606 121.46C144.944 120.957 146.48 118.878 146.036 116.815C145.593 114.753 143.338 113.488 141 113.99C138.662 114.493 137.126 116.572 137.57 118.635C138.013 120.697 140.268 121.962 142.606 121.46Z"
        fill={colorScheme.eyePupil}
      />

      {/* Right nostril/eye feature dark (neutral) */}
      <path
        d="M198.39 121.96C200.23 122.8 202.04 122.25 205.58 121.12C209.25 119.95 211.09 119.36 211.26 118.33C211.67 115.86 204.19 110.6 199.03 112.86C197.43 113.56 195.18 115.37 195.25 117.74C195.31 119.71 196.93 121.29 198.39 121.96Z"
        fill="#0C0C0C"
      />

      {/* Right ear/fin nostril dark (neutral) */}
      <path
        d="M251.95 109.99C252.24 111.59 252.54 113.36 252.85 115.32C252.69 115.66 252.49 115.98 252.3 116.24C249.47 120.12 241.71 119.45 240.95 117.46C240.63 116.63 241.72 115.4 243.93 112.97C246.05 110.62 247.15 109.43 248.86 109.26C249.81 109.17 251 109.37 251.94 109.99H251.95Z"
        fill="#0C0C0C"
      />

      <defs>
        {/* Outer shell shadow gradient (neutral: transparent to black) */}
        <linearGradient
          id={`${prefix}_outerShadow`}
          x1="158.81"
          y1="69.72"
          x2="158.81"
          y2="435.44"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.54" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Body outer sphere gradient (color-specific, 3-stop horizontal) */}
        <linearGradient
          id={`${prefix}_bodyOuter`}
          x1="46.54"
          y1="339.7"
          x2="271.08"
          y2="339.7"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyOuterStart} />
          <stop offset="0.6" stopColor={colorScheme.bodyOuterMid} />
          <stop offset="0.99" stopColor={colorScheme.bodyOuterEnd} />
        </linearGradient>

        {/* Body dark ring shadow fade (neutral: transparent to black) */}
        <linearGradient
          id={`${prefix}_bodyDarkFade`}
          x1="157.59"
          y1="210.11"
          x2="157.59"
          y2="415.17"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.41" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Body inner bright sphere gradient (color-specific, 3-stop horizontal) */}
        <linearGradient
          id={`${prefix}_bodyInner`}
          x1="89.0801"
          y1="299.09"
          x2="229.26"
          y2="299.09"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyInnerStart} />
          <stop offset="0.6" stopColor={colorScheme.bodyInnerMid} />
          <stop offset="0.99" stopColor={colorScheme.bodyInnerEnd} />
        </linearGradient>

        {/* Body front sphere gradient (color-specific, 3-stop diagonal) */}
        <linearGradient
          id={`${prefix}_bodyFront`}
          x1="98.36"
          y1="263.33"
          x2="352.26"
          y2="378.25"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyFrontStart} />
          <stop offset="0.86" stopColor={colorScheme.bodyFrontMid} />
          <stop offset="0.99" stopColor={colorScheme.bodyFrontEnd} />
        </linearGradient>

        {/* Neck/torso gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_neck`}
          x1="86.3883"
          y1="249.248"
          x2="220.024"
          y2="250.286"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.neckPrimary} />
          <stop offset="1" stopColor={colorScheme.neckSecondary} />
        </linearGradient>

        {/* Right arm/fin gradient (neutral: silver to white) */}
        <linearGradient
          id={`${prefix}_finRight`}
          x1="194.83"
          y1="180.29"
          x2="241.64"
          y2="180.29"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#B3B3B3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>

        {/* Head/body main silhouette gradient (neutral: dark to silver to white) */}
        <linearGradient
          id={`${prefix}_head`}
          x1="66.9661"
          y1="181.584"
          x2="257.241"
          y2="140.689"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1A1A1A" />
          <stop offset="0.51" stopColor="#B3B3B3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>

        {/* Left neck side highlight gradient (neutral: silver to white) */}
        <linearGradient
          id={`${prefix}_neckLeft`}
          x1="194.87"
          y1="126.41"
          x2="144.23"
          y2="126.41"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#B3B3B3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>

        {/* Right ear/spine fin gradient (neutral: silver to white) */}
        <linearGradient
          id={`${prefix}_earFin`}
          x1="234.28"
          y1="125.89"
          x2="254.57"
          y2="125.89"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#B3B3B3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>

        {/* Tail/lower fin gradient (neutral: silver to white) */}
        <linearGradient
          id={`${prefix}_tailFin`}
          x1="209.81"
          y1="232.01"
          x2="249.75"
          y2="232.01"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#B3B3B3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
      </defs>
    </svg>
  );
});

MagmaPawnComponent.displayName = 'MagmaPawnComponent';

export default MagmaPawnComponent;
